import { uaRuRouteNames } from './routes.js';
import {
  getLanguageSpecificRoutes,
  localePath,
  buildSard,
  DEFAULT_LANGUAGES
} from '@/utils/i18nRoutes';
import Router from 'vue-router';
import * as Sentry from '@sentry/vue';
import { setupGtmPageTracking } from '@/plugins/gtm-page-tracking';
import { RegionPlugin } from '@/plugins/region';
import { Capacitor } from '@capacitor/core';
import { useDeepLinking } from '@/composables/capacitor/useDeepLinking';
import { useLocalStorage } from '@/composables/useLocalStorage.js';
import { infoPageRegistry } from '@/feature/info-pages/registry';

const layoutMap = {
  Profile: [
    'dashboard',
    'accountSettings',
    'questions',
    'questionDetail',
    'appointments',
    'appointmentDetail',
    'profileDialog',
    'prevention',
    'preventionList',
    'medicalProcedure',
    'profileExplore',
    'profileExploreDetail',
    'benefitManagement',
    'vitalSigns'
  ],
  ProfileLock: ['dashboardLock'],
  RegionSelector: ['regionSelector'],
  Homepage: ['index', 'healthboxLanding', 'contactPage', 'careerPage', 'careerPageDetail'],
  ServicePage: ['servicePage'],
  // Derived from the registry so every info page gets InfoPageLayout automatically.
  InfoPage: infoPageRegistry.map(cfg => cfg.name)
};

const baseRouteName = routeName => routeName.split('___')[0];

function getLayoutComponent(routeName) {
  if (layoutMap.Profile.includes(routeName)) {
    return 'ProfileLayout';
  }
  if (layoutMap.ProfileLock.includes(routeName)) {
    return 'ProfileLock';
  }
  if (layoutMap.RegionSelector.includes(routeName)) {
    return 'RegionSelectorLayout';
  }
  if (layoutMap.Homepage.includes(routeName)) {
    return 'HomepageLayout';
  }
  if (layoutMap.ServicePage.includes(routeName)) {
    return 'ServicePageLayout';
  }
  if (layoutMap.InfoPage.includes(routeName)) {
    return 'InfoPageLayout';
  }
  return 'DefaultLayout';
}

// Captured once at startup — must stay stable for the lifetime of the router,
// because the route table (and which language gets the no-prefix path) is fixed here.
export const ROUTER_DEFAULT_LANGUAGE = DEFAULT_LANGUAGES[RegionPlugin.getRegion()];

const languageSpecificRoutes = getLanguageSpecificRoutes(ROUTER_DEFAULT_LANGUAGE);

const flatRoutes = languageSpecificRoutes.map(route => ({
  ...route,
  meta: {
    ...route.meta,
    layout: getLayoutComponent(baseRouteName(route.name)),
    name: baseRouteName(route.name)
  }
}));

const router = new Router({
  mode: 'history',
  routes: flatRoutes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    const excludedRoutes = ['socialLogin'];

    /* eslint-disable id-length */
    if (to.hash && !excludedRoutes.includes(to.meta.name)) {
      return { selector: to.hash, behavior: 'smooth', offset: { x: 0, y: 110 } };
    }

    // On iOS WKWebView (Capacitor), window.scrollTo() is animated — the slide
    // from the previous route's scroll position to 0 is visible. For the dialog
    // page we do an instant documentElement.scrollTop = 0 in beforeMount instead.
    if (Capacitor.isNativePlatform() && to.meta.name === 'profileDialog') {
      return false;
    }

    return { x: 0, y: 0 };
    /* eslint-enable */
  }
});

// Vital-signs page needs cross-origin isolation (COOP/COEP) for Shen.AI's
// WASM SharedArrayBuffer. CloudFront returns those headers only for the
// `/dashboard/vital-signs*` path; SPA navigation reuses the original
// document, so the browser never picks them up. Force a full reload on
// entry/exit so the document is fetched fresh with (or without) the
// isolation headers. Native (Capacitor) doesn't need this — the native
// SDK uses iOS/Android camera APIs, not WASM.
const VITAL_SIGNS_PATH_RE = /\/(dashboard\/vital-signs|vital-signs-public)(\/|$)/;

router.beforeEach(async (to, from, next) => {
  if (
    typeof window !== 'undefined' &&
    window.location.hostname.startsWith('vital-signs.') &&
    to.path === '/'
  ) {
    next({ name: 'vitalSignsPublic___cs', query: to.query, replace: true });
    return;
  }

  // Skip on initial page load (`from.name === null`) — the document is already
  // fresh from the network, so reloading would loop forever.
  if (!Capacitor.isNativePlatform() && from.name !== null) {
    const enteringVitalSigns =
      VITAL_SIGNS_PATH_RE.test(to.path) && !VITAL_SIGNS_PATH_RE.test(from.path);
    const leavingVitalSigns =
      !VITAL_SIGNS_PATH_RE.test(to.path) && VITAL_SIGNS_PATH_RE.test(from.path);
    if (enteringVitalSigns || leavingVitalSigns) {
      window.location.assign(to.fullPath);
      return;
    }
  }

  const newLanguage = to.meta.languageKey;
  const routeName = to.name?.split('___')[0];
  const defaultLanguage = ROUTER_DEFAULT_LANGUAGE;
  const isMobileApp = Capacitor.isNativePlatform();

  const langPrefixRe = /^\/(cs|sk|hu|en|uk|ru)(?=\/|$)/i;
  const normalizedPath = to.path.split('?')[0].replace(langPrefixRe, '');
  const isMastercardAttempt = /^\/mastercard(?:\/|$)/i.test(normalizedPath);

  const store = router.app.$store;

  if (store.$authReady) {
    await store.$authReady;
  }

  const isLoggedIn = store.state.auth.isLoggedIn;
  const hasMfaChallenge = store.state.auth.hasMfaChallenge;

  const { getLS } = useLocalStorage();
  const capacitorRegion = getLS('cap.region');

  // Mobile: redirect to region selector if no region has been chosen yet
  if (isMobileApp && !capacitorRegion) {
    if (!from.name?.startsWith('regionSelector') && !to.name?.startsWith('regionSelector')) {
      next(
        localePath({
          name: 'regionSelector',
          language: 'cs',
          defaultLanguage: 'cs'
        })
      );
      return;
    }
  }

  // Mobile: redirect to login if region is set but the user is not logged in
  if (isMobileApp && capacitorRegion && !isLoggedIn && !store.state.auth.isBusy) {
    const publicRoutes = ['login', 'lostPassword', 'registration'];
    const isPublicRoute = name => publicRoutes.some(prefix => name?.startsWith(prefix));
    if (!isPublicRoute(from.name) && !isPublicRoute(to.name)) {
      const lang = capacitorRegion === 'cz' ? 'cs' : capacitorRegion;
      next(localePath({ name: 'login', language: lang, defaultLanguage: lang }));
      return;
    }
  }

  // Mobile: redirect unmatched routes to login (or region selector)
  if (isMobileApp && to.name === null) {
    if (capacitorRegion) {
      const lang = capacitorRegion === 'cz' ? 'cs' : capacitorRegion;
      next(localePath({ name: 'login', language: lang, defaultLanguage: lang }));
    } else {
      next(
        localePath({
          name: 'regionSelector',
          language: 'cs',
          defaultLanguage: 'cs'
        })
      );
    }
    return;
  }

  if (newLanguage && router.app?.$i18n) {
    router.app.$i18n.locale = newLanguage;
  }

  if (!isMobileApp) {
    // Redirect mastercard deep-links to the HRB Health route
    if (isMastercardAttempt) {
      store.commit('general/cameFromMastercard', true);
      next(
        localePath({ name: 'hrbHealth', query: to.query, defaultLanguage: router.app.$i18n.locale })
      );
      return;
    }

    // Redirect unauthenticated users who entered a profile route directly (no referrer)
    if (
      from.name === null &&
      to.meta.layout === 'ProfileLayout' &&
      !isLoggedIn &&
      !store.state.auth.isBusy
    ) {
      next(
        localePath({
          name: 'login',
          language: router.app.$i18n.locale,
          defaultLanguage,
          query: {
            sard: buildSard({ query: to.query, params: to.params, page: to.name.split('___')[0] })
          }
        })
      );
      return;
    }

    // Redirect unmatched routes to registration
    if (to.name === null) {
      next(
        localePath({
          name: 'registration',
          query: { sard: buildSard({ query: to.query, params: to.params }) }
        })
      );
      return;
    }

    // Redirect unauthenticated users away from profile/lock routes
    const isProfile = to.meta.layout === 'ProfileLayout';
    const isLock = to.meta.layout === 'ProfileLock';
    if ((isProfile || isLock) && !isLoggedIn && !hasMfaChallenge && !store.state.auth.isBusy) {
      next({
        ...localePath({ name: 'login', language: router.app.$i18n.locale, defaultLanguage }),
        replace: true
      });
      return;
    }

    // Allow MFA users through to the lock screen
    if (isLock && hasMfaChallenge) {
      next();
      return;
    }
  }

  // Redirect UA/RU users to their default language for unsupported routes
  if (['uk', 'ru'].includes(newLanguage) && !uaRuRouteNames.includes(routeName)) {
    next(
      localePath({
        name: routeName,
        params: to.params,
        query: to.query,
        language: defaultLanguage,
        defaultLanguage,
        languageSpecificRoutes
      })
    );
    return;
  }

  // Redirect users with incomplete or unverified accounts to the lock screen
  const user = router.app.$auth ? router.app.$auth.user : store.state.auth.user;
  const allowUser = !!(user?.email?.length && user?.legalDocuments?.length);
  const isEmailUnverified = user && !user.verify.email;

  const isProfileOrLock = to.meta.layout === 'ProfileLayout' || to.meta.layout === 'ProfileLock';
  if (isProfileOrLock && isLoggedIn && !hasMfaChallenge && (!allowUser || isEmailUnverified)) {
    const isOnLock = to.meta.layout === 'ProfileLock';
    const wasOnLock = from.meta.layout === 'ProfileLock';

    // Email verification must always be reachable — blocking it means the token is never consumed and the user is permanently stuck on the lock screen.
    if (routeName === 'verifyEmail') {
      next();
      return;
    }

    if (isOnLock) {
      next();
      return;
    }

    if (wasOnLock) {
      next(false);
      return;
    }

    next(
      localePath({
        name: 'dashboardLock',
        language: router.app.$i18n.locale,
        defaultLanguage,
        query: to.query.sard
          ? { sard: to.query.sard }
          : {
              sard: buildSard({ query: to.query, params: to.params, page: to.name.split('___')[0] })
            }
      })
    );
    return;
  }

  next();
});

router.afterEach((to, from) => {
  setupGtmPageTracking(router, to);
  if (to.name?.split('___')[0] === 'registration') {
    router.app.$store.commit(
      'profile/registration/setRegistrationReferenceLink',
      from.name !== null ? from.name.split('___')[0] : document.referrer
    );
  }
});

const isNavigationFailure = err => err && err._isRouter;

const originalPush = router.push;
router.push = function push(loc) {
  return originalPush.call(this, loc).catch(err => {
    if (!isNavigationFailure(err)) {
      throw err;
    }
  });
};

const originalReplace = router.replace;
router.replace = function replace(loc) {
  return originalReplace.call(this, loc).catch(err => {
    if (!isNavigationFailure(err)) {
      throw err;
    }
  });
};

router.onError(error => {
  const isChunkError = /Loading chunk|Importing a module|Failed to fetch dynamically imported/.test(
    error.message
  );
  if (isChunkError) {
    const lastReload = sessionStorage.getItem('chunk-reload');
    const now = Date.now();
    if (!lastReload || now - Number(lastReload) > 10000) {
      sessionStorage.setItem('chunk-reload', String(now));
      window.location.reload();
      return;
    }
    // Reload already attempted — user is stuck, report to Sentry
    Sentry.withScope(scope => {
      scope.setTag('chunk-recovery', 'failed');
      scope.setLevel('fatal');
      Sentry.captureException(
        new Error(`Chunk load failed after retry and reload: ${error.message}`)
      );
    });
  }
});

if (Capacitor.isNativePlatform()) {
  useDeepLinking(router);
}

export default router;
